import React, { useState, useMemo, useEffect } from 'react';
import { useQuery } from '@apollo/client';
import getEstimatorJobs from './getEstimatorJobs.graphql';

const RED = '#D32F2F';
const INK = '#1F2733';
const GREY = '#5B6472';
const BORDER = '#E6E8EC';
const FONT = "'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif";

const HOURS = [1, 2, 3, 4, 6, 8];
const VISIT_FEE = 10;     // flat "visit fee" added to the whole bill
const FEE_RATE = 0.10;    // +10% on the labour subtotal
const BOOK_URL = '/category?type=all';

export default function EstimatorDropdown() {
  const [open, setOpen] = useState(false);
  const [catId, setCatId] = useState(null);
  const [hours, setHours] = useState(2);

  // Compact the trigger on small screens so it never crowds out the hamburger menu.
  const [compact, setCompact] = useState(false);
  useEffect(() => {
    const check = () => setCompact(typeof window !== 'undefined' && window.innerWidth <= 1199);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  // Jobs + their starting (minimum) per-hour rate come straight from the admin.
  const { data, loading } = useQuery(getEstimatorJobs, { ssr: false });
  const jobs = (data && data.getEstimatorJobs && data.getEstimatorJobs.results) || [];

  // default to the first priced job once the list loads
  const selected = useMemo(() => {
    if (!jobs.length) return null;
    const byId = catId != null ? jobs.find((j) => j.categoryId === catId) : null;
    return byId || jobs.find((j) => j.hasPrice) || jobs[0];
  }, [jobs, catId]);

  const amount = selected && selected.hasPrice && selected.minRate
    ? Math.round(selected.minRate * hours * (1 + FEE_RATE) + VISIT_FEE)
    : null;
  const currency = (selected && selected.currency) || 'USD';
  const sign = currency === 'USD' ? '$' : '';

  const field = { width: '100%', padding: '10px 12px', borderRadius: 10, border: `1px solid ${BORDER}`, fontSize: 14, marginTop: 6, fontFamily: FONT, color: INK, background: '#fff' };
  const label = { fontSize: 12, fontWeight: 600, color: GREY, marginTop: 12, display: 'block' };

  return (
    <div style={{ position: 'relative', fontFamily: FONT, marginLeft: compact ? 8 : 14 }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        style={{ display: 'inline-flex', alignItems: 'center', gap: compact ? 5 : 6, background: RED, color: '#fff', border: 'none', borderRadius: 999, padding: compact ? '8px 13px' : '10px 16px', fontSize: compact ? 13 : 14.5, fontWeight: 800, letterSpacing: .2, cursor: 'pointer', fontFamily: FONT, boxShadow: '0 6px 18px rgba(211,47,47,.35)', whiteSpace: 'nowrap' }}>
        💸 {compact ? 'Estimate' : 'Estimate price'} <span style={{ fontSize: 13 }}>▾</span>
      </button>

      {open && (
        <>
          <div onClick={() => setOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 40 }} />
          <div style={compact
            ? { position: 'fixed', top: 'calc(var(--frontPage-header-height) + 6px)', left: 12, right: 12, zIndex: 50, width: 'auto', maxWidth: 380, margin: '0 auto', background: '#fff', borderRadius: 16, border: `1px solid ${BORDER}`, boxShadow: '0 16px 44px rgba(0,0,0,.22)', padding: 18, color: INK }
            : { position: 'absolute', top: 'calc(100% + 10px)', right: 0, zIndex: 50, width: 300, background: '#fff', borderRadius: 16, border: `1px solid ${BORDER}`, boxShadow: '0 16px 44px rgba(0,0,0,.18)', padding: 18, color: INK }}>
            <div style={{ fontWeight: 800, fontSize: 16, marginBottom: 4 }}>Estimate your job price</div>

            <label style={label}>Job</label>
            <select
              value={selected ? selected.categoryId : ''}
              onChange={(e) => setCatId(Number(e.target.value))}
              style={field}
              disabled={loading || !jobs.length}>
              {!jobs.length && <option>{loading ? 'Loading…' : 'No jobs available'}</option>}
              {jobs.map((j) => <option key={j.categoryId} value={j.categoryId}>{j.name}</option>)}
            </select>

            <label style={label}>How long?</label>
            <select value={hours} onChange={(e) => setHours(Number(e.target.value))} style={field}>
              {HOURS.map((h) => <option key={h} value={h}>{h} hour{h > 1 ? 's' : ''}</option>)}
            </select>

            <div style={{ marginTop: 16, padding: '14px 0', borderTop: `1px solid ${BORDER}`, textAlign: 'center' }}>
              {loading ? (
                <span style={{ color: GREY }}>Calculating…</span>
              ) : amount != null ? (
                <div>
                  <div style={{ fontSize: 12, color: GREY }}>Estimated total</div>
                  <div style={{ fontSize: 30, fontWeight: 800, color: RED }}>{sign}{amount}</div>
                </div>
              ) : (
                <span style={{ color: GREY, fontSize: 14 }}>Get a free quote from local pros.</span>
              )}
            </div>

            {/* Two paths — small jobs (Book now) vs large projects (Get a quote).
                On mobile this is where these CTAs live (the header bar hides them). */}
            <div style={{ display: 'flex', gap: 8 }}>
              <a href={BOOK_URL} style={{ flex: 1, textAlign: 'center', background: RED, color: '#fff', fontWeight: 700, fontSize: 14.5, padding: '12px', borderRadius: 12, textDecoration: 'none' }}>
                Book now
              </a>
              <a href="/post-a-project" style={{ flex: 1, textAlign: 'center', background: '#fff', color: '#0A7C70', border: '1.5px solid #0A7C70', fontWeight: 700, fontSize: 14.5, padding: '11px', borderRadius: 12, textDecoration: 'none' }}>
                Get a quote
              </a>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
